import { InternalOptions, Options, State } from 'define/types'
import { attachDrag, OnDragEvent } from 'ui/widget/drag'
import css from 'ui/widget/widget.module.scss'
import { Destructor, Destructors } from 'util/destructors'
import { scrollLock } from 'util/scrollLock'
import { storage } from 'util/storage'

const minLeft = 16

function allow() {
  // @ts-ignore
  const share = navigator.canShare ? 'web-share' : ''
  return ['clipboard-write', 'camera', share].filter(s => !!s).join('; ')
}

function getPositionClass(position: string | unknown): string {
  switch (position) {
    case 'left':
      return css.left as string
    case 'center':
      return css.center as string
    default:
      return css.right as string
  }
}

export class Elements {
  private _ready = false
  private _state: State | undefined

  private options: Options = {}
  private container?: HTMLElement

  badge: HTMLDivElement = document.createElement('div')
  private handle: HTMLDivElement = document.createElement('div')
  private border: HTMLDivElement = document.createElement('div')
  frame: HTMLIFrameElement = document.createElement('iframe')
  private root: HTMLDivElement = document.createElement('div')

  private destructors = new Destructors()
  private drag = { left: 0, update: 0 }

  create(internal: InternalOptions, options: Options) {
    const host = window.showoff?.host
    if (!host) throw new Error('no host')

    this.options = options
    this.container = internal.container

    const glass = document.createElement('div')
    glass.classList.add(css.glass!)
    this.badge.classList.add(css.badge!)
    this.badge.innerText = ''
    this.handle.classList.add(css.drag!)
    this.handle.innerHTML = this.dragSvg()
    this.border.classList.add(css.border!)
    this.frame.classList.add(css.frame!)
    this.frame.allow = allow()
    this.frame.src = host
    this.border.appendChild(this.frame)

    this.root.appendChild(glass)
    this.root.appendChild(this.badge)
    this.root.appendChild(this.handle)
    this.root.appendChild(this.border)

    this.root.className = ''
    this.vars()

    if (this.container) this.container.appendChild(this.root)
    else document.body.appendChild(this.root)

    if (this.container) this.destructors.add(this.attachResizeContainer(this.container))
    else this.destructors.add(this.attachResizeWindow())

    this.destructors.add(attachDrag(this.handle, this.root, this.onDrag))

    this._ready = true
    this.window(this.state)
  }

  destroy() {
    this._ready = false
    this._state = undefined
    this.destructors.destroy()
    this.root.remove()
    this.badge = document.createElement('div')
    this.handle = document.createElement('div')
    this.border = document.createElement('div')
    this.frame = document.createElement('iframe')
    this.root = document.createElement('div')
    this.drag = { left: 0, update: 0 }
  }

  get state(): State | undefined {
    return this._state
  }

  onFrame(width: number) {
    const w = width == null ? null : width + 'px'
    this.root.style.setProperty('--showoff-frame-width', w)
  }

  window(state: State | undefined) {
    this.state = state
  }

  private set state(state: State | undefined) {
    this._state = state

    if (!this._ready) return

    storage.window = state
    scrollLock.setMax(state === 'max')

    const { collapse_button_type } = this.options
    const widget = this.container ? css.embedded : css.widget
    const position = getPositionClass(this.options.position)
    const icon = collapse_button_type === 'icon' ? css.icon : ''
    this.root.className = [widget, position, icon, css[state || 'none']].filter(c => c).join(' ')
  }

  count(count: number) {
    this.badge.innerHTML = `${count}`
    this.badge.classList.toggle(css.show!, count > 0)
  }

  private vars() {
    const { collapse_button, collapse_button_text } = this.options.color ?? {}

    if (this.options.tsum) {
      this.root.style.setProperty('--showoff-badge', '#F94F0D')
      this.root.style.setProperty('--showoff-badge-text', '#FFFFFF')
      this.root.style.setProperty('--showoff-drag', '#1B1B1B52')
    } else if (this.options.theme === 'dark') {
      this.root.style.setProperty('--showoff-badge', '#FFFFFF')
      this.root.style.setProperty('--showoff-badge-text', '#1E1E1E')
      this.root.style.setProperty('--showoff-drag', '#FFFFFF7A')
    } else {
      this.root.style.setProperty('--showoff-badge', '#1E1E1E')
      this.root.style.setProperty('--showoff-badge-text', '#FFFFFF')
      this.root.style.setProperty('--showoff-drag', '#1E1E1EE0')
    }

    if (collapse_button) {
      this.root.style.setProperty('--showoff-collapse-button', collapse_button)
    }
    if (collapse_button_text) {
      this.root.style.setProperty('--showoff-collapse-button-text', collapse_button_text)
    }
  }

  private attachResizeContainer(container: HTMLElement) {
    const ro = new ResizeObserver(() => {
      const height = container.clientHeight
      this.root.style.setProperty('--showoff-container-height', `${height}px`)
    })
    ro.observe(container)
    return () => ro.disconnect()
  }

  private attachResizeWindow(): Destructor {
    const update = () => {
      const height = window.innerHeight.toFixed()
      this.root.style.setProperty('--showoff-app-height', `${height}px`)
      this.initPosition()
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }

  private initPosition() {
    const right = storage.right
    const left = storage.left
    this.position(left, right)
  }

  private position(left: number | undefined, right: number | undefined) {
    if (right == null && left == null) {
      const position = this.options.position
      if (position === 'left') {
        left = 0
      } else if (position === 'center') {
        const view = document.documentElement.getBoundingClientRect()
        const rect = this.root.getBoundingClientRect()
        left = (view.width - rect.width) / 2
      } else {
        right = 0
      }
    }
    left = this.fixLeft(left)
    right = left == null ? this.fixRight(right) : undefined
    this.root.style.setProperty('--showoff-left', left == null ? '' : `${left}px`)
    this.root.style.setProperty('--showoff-right', right == null ? '' : `${right}px`)
  }

  private fixLeft(left: number | undefined): number | undefined {
    if (left == null) return undefined
    const view = document.documentElement.getBoundingClientRect()
    const rect = this.root.getBoundingClientRect()
    const max = view.width - rect.width - minLeft
    return Math.max(minLeft, Math.min(max, Math.round(left)))
  }

  private fixRight(right: number | undefined): number | undefined {
    if (right == null) return undefined
    const view = document.documentElement.getBoundingClientRect()
    const { width } = this.root.getBoundingClientRect()
    const max = view.width - width - minLeft
    return Math.max(minLeft, Math.min(max, Math.round(right)))
  }

  private readonly onDrag = ({ start, end, dx }: OnDragEvent) => {
    const view = document.documentElement.getBoundingClientRect()
    const rect = this.root.getBoundingClientRect()
    if (start) {
      this.handle.classList.add(css.active!)
      this.drag.left = rect.left
    }
    let left: number | undefined = this.drag.left + dx
    left = this.fixLeft(left) ?? 0
    cancelAnimationFrame(this.drag.update)
    this.drag.update = requestAnimationFrame(() => {
      this.position(left, undefined)
    })
    if (end) {
      this.handle.classList.remove(css.active!)
      const center = left + rect.width / 2
      const pin = center > view.width / 2
      const right = pin ? view.width - rect.width - left : undefined
      left = right == null ? left : undefined
      storage.left = this.fixLeft(left)
      storage.right = this.fixRight(right)
      this.position(left, right)
      cancelAnimationFrame(this.drag.update)
    }
  }

  private dragSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <circle cx="8.95" cy="5.89" r="2" color="currentColor" />
      <circle cx="15.05" cy="5.89" r="2" color="currentColor" />
      <circle cx="15.05" cy="12" r="2" color="currentColor" />
      <circle cx="8.95" cy="12" r="2" fill="currentColor" />
      <circle cx="8.95" cy="18.11" r="2" fill="currentColor" />
      <circle cx="15.05" cy="18.11" r="2" fill="currentColor" />
    </svg>`
  }
}
